import { EssentialPromoteCard } from 'components/promote-card/essential-promote-card';
import type { IntlKey } from 'locales';
import type { AnalyticCategoryActions } from 'models';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { SelectedProductVariation } from 'models/productsReferential';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { AppState } from 'state/state.model';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { isSelectedProductVariationValid } from 'state/workspaces/workspaces/workspaces.selector';

import { ClientSearchButton } from './ClientSearchButton';
import { ProductEmptyList } from './ProductEmptyList';
import { ProductList } from './ProductList';
import { filterSearchResult } from './ProductSearch.helper';
import { ProductSearchInput } from './ProductSearchInput';
import { ProductSelectorTabs } from './ProductSelectorTabs';

export type ProductVariationSelectionAutoFocusType = 'searchInput' | 'confirmButton';

export interface ProductVariationSelectorProps {
  workspace: WorkspaceWithProductSelector;
  autoFocus?: ProductVariationSelectionAutoFocusType;
  canSelectClient?: boolean;
  canSelectPayoffType?: boolean;
  selectedProductVariation: Partial<SelectedProductVariation>;
  selectedClient: Nullable<ISalesCounterpart>;
  onProductVariationSelected(product: Partial<SelectedProductVariation>, quickStart: boolean): void;
  onSelectClientClick(): void;
  onClearClientClick(): void;
  onConfirmAndCreateClick(): void;
}

export const ProductVariationSelector: FC<ProductVariationSelectorProps> = ({
  workspace,
  autoFocus = 'searchInput',
  canSelectClient = false,
  canSelectPayoffType = false,
  selectedProductVariation,
  selectedClient,
  onProductVariationSelected,
  onSelectClientClick,
  onClearClientClick,
  onConfirmAndCreateClick,
}) => {
  const intl = useIntl();
  const isSelectedProductValid = useSelector((state: AppState) =>
    isSelectedProductVariationValid(state, workspace, intl),
  );

  return (
    <div className="card h-100">
      <div className="card-header pb-2">
        <h1 className="card-title fw-bold line-height-1 mb-1">
          <FormattedMessage id={`productSelector.${workspace}.title` as IntlKey} />
        </h1>
        <p className="fs-16 text-secondary m-0">
          <FormattedMessage id={`productSelector.${workspace}.subtitle` as IntlKey} />
        </p>
      </div>
      <div className="card-body d-flex flex-column pb-3">
        <ProductSearchInput
          className="flex-fill"
          autoFocus={autoFocus === 'searchInput'}
          workspaceType={workspace}
          inputPlaceholder="productSelector.search.inputPlaceholder"
          onFilter={filterSearchResult}
          onEnter={productVariation => onProductVariationSelected(productVariation, false)}
        >
          {({ searchContext, cursorAtIndex, setCursorAtIndex, onClearSearch }) =>
            searchContext.searchText === '' ? (
              <ProductSelectorTabs
                className="h-100"
                workspace={workspace}
                canSelectPayoffType={canSelectPayoffType}
                selectedProductVariation={selectedProductVariation}
                onProductVariationSelected={onProductVariationSelected}
              />
            ) : (
              <ProductList
                className="h-100"
                productVariations={searchContext.matchedProductVariations}
                selectedProductVariation={selectedProductVariation}
                cursorAtIndex={cursorAtIndex}
                onProductVariationSelected={(productVariation, isQuickStart, index) => {
                  logAnalytics.trackEvent(
                    `Primary market product selector ${workspace}` as keyof AnalyticCategoryActions,
                    'Select product',
                    `${selectedProductVariation.productType} ${selectedProductVariation.productSubType} ${selectedProductVariation.wrapper}`,
                  );
                  if (isQuickStart === false) {
                    setCursorAtIndex(index);
                  }
                  onProductVariationSelected(productVariation, isQuickStart);
                }}
                tabType="catalogue"
                workspace={workspace}
              >
                <ProductEmptyList onClearSearchClick={onClearSearch} />
              </ProductList>
            )
          }
        </ProductSearchInput>
      </div>
      <div className="card-footer d-flex justify-content-end">
        {canSelectClient && (
          <ClientSearchButton
            selectedClient={selectedClient}
            onSearchClick={onSelectClientClick}
            onRemoveClick={onClearClientClick}
          />
        )}
        <button
          type="button"
          data-e2e="product-selector-confirm-and-create"
          className="btn btn-xl btn-icon-end btn-primary ms-2"
          autoFocus={autoFocus === 'confirmButton'}
          disabled={!isSelectedProductValid}
          onClick={onConfirmAndCreateClick}
        >
          <FormattedMessage id="productSelector.confirmAndCreate" />
          <i className="icon">chevron_right</i>
        </button>
      </div>

      <EssentialPromoteCard className="mx-4 mb-4" />
    </div>
  );
};
